import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useRepository } from '@city-sage/aurum-avis-labs-components';
import { Button } from '@city-sage/aurum-avis-labs-components';
import { QueryKeys } from '../../../lib/QueryKeys';
import {
  X,
  Share2,
  Play,
  MapPin,
  Clock,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useReactions } from '../../../lib/useReactions';
import { Avatar } from '../../Avatar';
import { LikeButton } from '../../actions/LikeButton';
import { CommentButton } from '../../actions/CommentButton';
import { ShareButton } from '../../actions/ShareButton';
import { cn, Image } from '@city-sage/aurum-avis-labs-components';
import { Badge } from '@city-sage/aurum-avis-labs-components';
import { Itinerary, StopStateEnum } from '@city-sage/core-client';
import { MarkdownRenderer } from '../../MarkdownRenderer';
import { useMultipleDayStops } from '../../../hooks/useMultipleDayStops';

// Z-index constants for proper layering
const Z_INDICES = {
  BACKGROUND: 0,
  TAP_AREAS: 10,
  PROGRESS_BARS: 20,
  AUTHOR_INFO: 20,
  MAIN_CONTENT: 25,
  NAVIGATION_ARROWS: 30,
  SIDE_ACTIONS: 40,
  BOTTOM_CTA: 40,
} as const;

interface ItineraryPreviewStoryProps {
  itinerary: Itinerary;
  isActive: boolean;
}

export function ItineraryPreviewStory({
  itinerary,
  isActive,
}: ItineraryPreviewStoryProps) {
  const { api } = useRepository();

  // State for slideshow
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const progressRef = useRef<NodeJS.Timeout | null>(null);
  const holdTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [progressValue, setProgressValue] = useState(0);
  const [isLooping, setIsLooping] = useState(false);

  // Fetch days data
  const { data: days, isLoading: isLoadingDays } = useQuery({
    queryKey: QueryKeys.itinerary.days(itinerary.id),
    queryFn: () =>
      api.findAllItineraryDay({
        itineraryId: itinerary.id,
        sort: JSON.stringify(['number', 'ASC']),
        // TODO this is hardcoded for now, we need to fix this
        limit: 1000,
      }),
    enabled: !!itinerary.id,
  });

  const { data: allStops } = useMultipleDayStops({
    itineraryId: itinerary.id,
    days,
    states: [StopStateEnum.Accepted],
  });


  // Use reactions hook
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'itinerary',
    reactableId: itinerary.id,
  });

  // Create slides array: overview + stops with images, sorted by day order
  const slides = [
    { type: 'overview', data: null },
    ...(allStops?.allStops
      ?.filter((stop) => stop.imageUrl)
      .sort((a, b) => {
        // Sort stops by the earliest day they belong to
        const getEarliestDay = (stop: any) => {
          if (!stop.dayIds?.length || !days?.length) return Infinity;
          const earliestDayNumber = Math.min(
            ...stop.dayIds.map((dayId: string) => {
              const day = days.find((d) => d.id === dayId);
              return day?.number ?? Infinity;
            })
          );
          return earliestDayNumber;
        };

        return getEarliestDay(a) - getEarliestDay(b);
      })
      .map((stop) => ({ type: 'stop', data: stop })) || []),
  ];

  const currentSlide = slides[currentSlideIndex];
  const isOverview = currentSlide?.type === 'overview';

  // Auto-advance with smooth progress (only when active)
  useEffect(() => {
    if (!isActive || isHolding || slides.length <= 1) return;

    const duration = 5000; // 5 seconds per slide
    const interval = 50; // Update every 50ms for smooth progress

    progressRef.current = setInterval(() => {
      setProgressValue((prev) => {
        const newValue = prev + (interval / duration) * 100;
        if (newValue >= 100) {
          setCurrentSlideIndex((prevIndex) => {
            const nextIndex = (prevIndex + 1) % slides.length;
            if (nextIndex === 0 && prevIndex === slides.length - 1) {
              setIsLooping(true);
              setTimeout(() => setIsLooping(false), 100);
            }
            return nextIndex;
          });
          return 0;
        }
        return newValue;
      });
    }, interval);

    return () => {
      if (progressRef.current) {
        clearInterval(progressRef.current);
      }
    };
  }, [isActive, isHolding, slides.length, currentSlideIndex]);

  // Reset progress when slide changes or when becoming active
  useEffect(() => {
    setProgressValue(0);
    setCurrentSlideIndex(0);
  }, [isActive, itinerary.id]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (progressRef.current) {
        clearInterval(progressRef.current);
      }
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
    };
  }, []);

  const handlePrevious = useCallback(() => {
    // No loop from first to last - stop at first slide
    setCurrentSlideIndex((prev) => Math.max(0, prev - 1));
    // Reset progress when manually navigating
    setProgressValue(0);
  }, []);

  const handleNext = useCallback(() => {
    // Loop from last to first
    setCurrentSlideIndex((prev) => (prev + 1) % slides.length);
    // Reset progress when manually navigating
    setProgressValue(0);
  }, [slides.length]);

  // Click handlers for left/right edge navigation
  const handleLeftClick = () => {
    handlePrevious(); // No loop from first to last
  };

  const handleRightClick = () => {
    handleNext(); // Loop from last to first
  };

  // Hold detection for pausing slideshow
  const handleHoldStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Clear any existing timeout
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }

    // Only pause after a delay to differentiate between click and hold
    holdTimeoutRef.current = setTimeout(() => {
      setIsHolding(true);
    }, 300); // 300ms delay to distinguish hold from click
  };

  const handleHoldEnd = (e?: React.MouseEvent | React.TouchEvent) => {
    // Clear the hold timeout - if released before timeout, it was just a click
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    // If we were holding, resume slideshow after a small delay
    if (isHolding) {
      setTimeout(() => setIsHolding(false), 100);
    }
  };

  if (isLoadingDays || !itinerary) {
    return (
      <div className="h-full w-full flex items-center justify-center bg-black">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const totalStops = allStops?.allStops.length || 0;

  // Get current image and data
  const getCurrentImage = () => {
    if (isOverview) {
      return itinerary.imageUrl || '/placeholder.svg';
    }
    return currentSlide.data?.imageUrl || '/placeholder.svg';
  };

  const getCurrentImageEntity = () => {
    if (isOverview) {
      return itinerary.image;
    }
    return currentSlide.data?.image;
  };

  const getCurrentImageAlt = () => {
    if (isOverview) {
      return itinerary.title;
    }
    return currentSlide.data?.title || 'Stop';
  };

  // Get day number for current stop
  const getCurrentDayNumber = () => {
    if (!currentSlide.data?.dayIds?.[0] || !days) return 1;
    const day = days.find((day) => day.id === currentSlide.data.dayIds[0]);
    return day?.number || 1;
  };

  // Get location string for current stop
  const getCurrentLocation = () => {
    if (!currentSlide.data?.address) return '';
    return [
      currentSlide.data?.place?.address?.city,
      currentSlide.data?.place?.address?.countryName,
    ]
      .filter(Boolean)
      .join(', ');
  };

  return (
    <div
      className="h-full w-full relative bg-background select-none"
      style={{ zIndex: Z_INDICES.BACKGROUND }}
    >
      {/* Background Images */}
      <div className="absolute inset-0">
        <div className="absolute inset-0">
          <Image
            key={getCurrentImage()}
            src={getCurrentImage()}
            image={getCurrentImageEntity()}
            alt={getCurrentImageAlt()}
            className="w-full h-full"
            imageClassName="w-full h-full"
          />
          {/* Enhanced gradient overlay for better text readability */}

          <div className="absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-black/50 to-transparent z-10" />
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/80 to-transparent z-10" />
        </div>
      </div>

      {/* Progress Bars */}
      {slides.length > 1 && (
        <div
          className="absolute top-4 left-4 right-4"
          style={{ zIndex: Z_INDICES.PROGRESS_BARS }}
        >
          <div className="flex gap-1">
            {slides.map((_, index) => (
              <div
                key={index}
                className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden backdrop-blur-md"
              >
                <motion.div
                  className="h-full bg-white rounded-full"
                  initial={{ width: '0%' }}
                  animate={{
                    width:
                      index < currentSlideIndex
                        ? '100%'
                        : index === currentSlideIndex
                        ? `${progressValue}%`
                        : '0%',
                  }}
                  transition={{
                    duration:
                      isLooping && index > currentSlideIndex
                        ? 0
                        : index < currentSlideIndex
                        ? 0
                        : 0.1,
                    ease: 'linear',
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Author Info */}
      <div
        className="absolute top-10 left-4 right-4"
        style={{ zIndex: Z_INDICES.AUTHOR_INFO }}
      >
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex items-center gap-3"
        >
          {itinerary.account && (
            <Link
              to="/users/$userId"
              params={{ userId: itinerary.account.id }}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-10 w-10 overflow-hidden rounded-full border-2 border-white/50">
                <Avatar account={itinerary.account} className="w-full h-full" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">
                  {itinerary.account.userName || 'Anonymous'}
                </div>
                <div className="text-white/70 text-xs">
                  @{itinerary.account.userName || 'anonymous'}
                </div>
              </div>
            </Link>
          )}
        </motion.div>
      </div>

      {/* Main Content */}
      <div
        className="absolute inset-0 flex flex-col p-6 pointer-events-none"
        style={{ zIndex: Z_INDICES.MAIN_CONTENT }}
      >
        <AnimatePresence mode="wait">
          {isOverview ? (
            <motion.div
              key="overview"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              className="text-center space-y-6 flex-1 flex flex-col justify-center pt-16 pb-24"
            >
              {/* Title */}
              <div className="space-y-3">
                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-white">
                  {itinerary.title}
                </h1>
                <div className="text-lg text-white/90 leading-relaxed max-w-md mx-auto line-clamp-3">
                  <MarkdownRenderer
                    content={
                      itinerary.shortDescription || itinerary.description || ''
                    }
                    className="text-white/90 text-lg"
                  />
                </div>
              </div>

              {/* Enhanced Stats */}
              <div className="flex justify-center gap-8">
                {[
                  {
                    label: 'Days',
                    value: itinerary.daysCount || days?.length || 0,
                    icon: Calendar,
                  },
                  { label: 'Stops', value: totalStops, icon: MapPin },
                ].map(({ label, value, icon: Icon }) => (
                  <motion.div
                    key={label}
                    className="text-center group"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="flex flex-col items-center gap-2">
                      <div className="h-12 w-12 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center group-hover:bg-white/20 transition-colors">
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {value}
                      </div>
                      <div className="text-xs text-white/70 uppercase tracking-wide">
                        {label}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          ) : (
            <div className="flex-1 flex flex-col justify-end pb-16">
              <motion.div
                key={`stop-${currentSlide.data?.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="space-y-3"
              >
                {/* Badges - Above Title */}
                <div className="flex gap-2 items-start">
                  {/* Day Badge - White */}
                  <Badge variant="secondary">
                    <Calendar className="h-3 w-3 mr-1" />
                    Day {getCurrentDayNumber()}
                  </Badge>

                  {/* Time & Duration Badge - Primary (Blue) */}
                  {(currentSlide.data?.startTime ||
                    currentSlide.data?.durationMinutes) && (
                    <Badge variant="secondary">
                      <Clock className="h-3 w-3 mr-1" />
                      {[
                        currentSlide.data?.startTime,
                        currentSlide.data?.durationMinutes &&
                          `${currentSlide.data.durationMinutes} min`,
                      ]
                        .filter(Boolean)
                        .join(' • ')}
                    </Badge>
                  )}
                </div>

                {/* Stop Title */}
                <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                  {currentSlide.data?.title}
                </h2>

                {/* Location - Closely below title */}
                {getCurrentLocation() && (
                  <div className="flex items-center gap-1.5 text-white/90 -mt-1">
                    <MapPin className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {getCurrentLocation()}
                    </span>
                  </div>
                )}

                {/* Description - First 3 lines only */}
                {currentSlide.data?.description && (
                  <div className="text-white/80 leading-relaxed text-sm max-w-sm line-clamp-3 pt-1 w-[90%]">
                    <MarkdownRenderer
                      content={currentSlide.data.description}
                      className="text-white/80 text-sm"
                    />
                  </div>
                )}
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>

      {/* Side Actions */}
      <div
        className="absolute bottom-32 right-6"
        style={{ zIndex: Z_INDICES.SIDE_ACTIONS }}
      >
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          className="flex flex-col gap-4 items-center"
        >
          {/* Share Button */}
          <ShareButton
            url={`${window.location.origin}/itineraries/${itinerary.id}`}
            title={itinerary.title}
            description={itinerary.shortDescription || itinerary.description}
            hashtags={[
              'CitySage',
              'Travel',
              'Itinerary',
              itinerary.title.replace(/\s+/g, ''),
            ]}
            variant="dark"
            layout="vertical"
            size="lg"
            shareMethod="dialog"
          />

          {/* Comment Button */}
          <CommentButton
            postableType="itinerary"
            postableId={itinerary.id}
            variant="dark"
            layout="vertical"
            size="lg"
          />

          {/* Like Button */}
          <LikeButton
            isLiked={isLiked}
            likeCount={likeCount}
            onToggleLike={toggleLike}
            isPending={isPending}
            variant="dark"
            layout="vertical"
            size="lg"
          />
        </motion.div>
      </div>

      {/* Navigation Arrows (Desktop) */}
      {slides.length > 1 && (
        <div
          className="hidden md:flex absolute inset-y-0 left-4 right-4 items-center justify-between pointer-events-none"
          style={{ zIndex: Z_INDICES.NAVIGATION_ARROWS }}
        >
          <Button
            variant="ghost"
            size="icon"
            onClick={handlePrevious}
            className="h-12 w-12 text-white rounded-full backdrop-blur-md bg-black/30 pointer-events-auto"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNext}
            className="h-12 w-12 text-white rounded-full backdrop-blur-md bg-black/30 pointer-events-auto"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </div>
      )}

      {/* Bottom CTA */}
      <div
        className="absolute bottom-6 left-6 right-6"
        style={{ zIndex: Z_INDICES.BOTTOM_CTA }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
          className="space-y-3"
        >
          <Link
            to={`/itineraries/${itinerary.id}`}
            className="pointer-events-auto"
          >
            <Button
              size="lg"
              className="w-full bg-white text-black hover:bg-white/90 font-semibold py-4 rounded-2xl shadow-lg backdrop-blur-md"
            >
              <Play className="h-5 w-5 mr-2" />
              Explore Full Itinerary
            </Button>
          </Link>
        </motion.div>
      </div>

      {/* Click areas for desktop navigation and hold to pause */}
      {slides.length > 1 && (
        <div
          className="absolute inset-0"
          style={{ zIndex: Z_INDICES.TAP_AREAS }}
        >
          {/* Left click area for desktop (20% of width) */}
          <button
            className="absolute left-0 top-0 h-full w-[20%] md:hover:bg-white/5 transition-colors"
            onClick={handleLeftClick}
            onMouseDown={handleHoldStart}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            aria-label="Previous slide"
          />
          {/* Middle area for hold to pause (60% of width) */}
          <div
            className="absolute left-[20%] top-0 h-full w-[60%]"
            onMouseDown={handleHoldStart}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            onTouchStart={handleHoldStart}
            onTouchEnd={handleHoldEnd}
            onTouchCancel={handleHoldEnd}
          />
          {/* Right click area for desktop (20% of width) */}
          <button
            className="absolute right-0 top-0 h-full w-[20%] md:hover:bg-white/5 transition-colors"
            onClick={handleRightClick}
            onMouseDown={handleHoldStart}
            onMouseUp={handleHoldEnd}
            onMouseLeave={handleHoldEnd}
            aria-label="Next slide"
          />
        </div>
      )}
    </div>
  );
}
